import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import * as User from '@/components/User';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import {
  learnerWidgetStyles,
  overflowWrapAnywhere,
} from '@/modules/learner/views/LearnerProfile/styles';
import { LearnerWidgetProps } from '@/modules/learner/views/LearnerProfile/types';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import {
  renderLearnerCover,
  renderLearnerInfoItem,
  renderLearnerProfilePicture,
} from './renderers';
import { getLearnerInfoItems } from './utils';

function LearnerInfoWidget(props: LearnerWidgetProps) {
  const { learner } = props;
  const { t } = useTranslation();
  const isAdmin = useAdminRoute();
  const currentUser = useUserContext();
  const isSelf = learner.id === currentUser.id;
  const { [FeatureFlags.EnableLearnerProfileJoinedDate]: isJoinedDateVisible } = useFeatureFlags();

  const items = useMemo(
    () => getLearnerInfoItems({ t, learner, isSelf, isAdmin, isJoinedDateVisible }),
    [t, learner, isSelf, isAdmin, isJoinedDateVisible],
  );

  return (
    <Stack className={cn(learnerWidgetStyles, 'overflow-hidden py-0 px-0')} direction="col">
      {renderLearnerCover(learner)}
      <User.Provider className="flex-col px-24" user={learner as any}>
        {renderLearnerProfilePicture(learner)}
        <Stack className="mt-24" direction="col" gapY={24}>
          <Stack direction="col" gapY={4}>
            <User.Name
              className={overflowWrapAnywhere}
              color="text-text-primary"
              variant="heading-m"
            />
            <User.UniqueId
              className={overflowWrapAnywhere}
              color="text-text-secondary"
              variant="body-short-02"
            />
          </Stack>
          {items.map(renderLearnerInfoItem)}
        </Stack>
      </User.Provider>
    </Stack>
  );
}

export default LearnerInfoWidget;
